import type {Env} from '@hconnect/common/hproduce'
import {CommonHeader, useThemeSettings} from '@hconnect/uikit/src/lib2'
import type {User, NavItem} from '@hconnect/uikit/src/lib2'
import {CommonHeaderGuidedTourProps} from '@hconnect/uikit/src/lib2/shell/header/CommonHeader'
import {getPlantName, useUpmPlantsContext} from '@hconnect/upm'
import {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation, useNavigate, matchPath} from 'react-router'

import {subPageDynamicImportMap} from '../../dynamicImports'
import {mainRoutes, MainRoutes, getUrl, RouteName, resolvePageKey} from '../../routing'
import {useTrackPlannerEvent} from '../helpers/trackPlannerEvents'
import {useOptimizedSchedulesStatsQuery, usePermissionsQuery} from '../hooks/api'
import {usePageAvailability} from '../hooks/usePageAvailability'
import {useUrlParam} from '../hooks/useUrlParam'

import {useLoginDetails} from './providers/LoginProvider'

type PlannerHeaderProps = {
  guidedTours?: CommonHeaderGuidedTourProps
}

export const PlannerHeader: React.FC<PlannerHeaderProps> = ({guidedTours}) => {
  const {t, i18n} = useTranslation()
  const plantCode = useUrlParam('plantCode')

  const upmPlants = useUpmPlantsContext()

  const trackPlannerEvent = useTrackPlannerEvent()

  const {loginDetails, logout} = useLoginDetails()
  const {data: permissions} = usePermissionsQuery()

  const navigate = useNavigate()
  const location = useLocation()
  const pathName = location.pathname

  const pageAvailability = usePageAvailability(plantCode)
  const {data: optimizedSchedulesStats} = useOptimizedSchedulesStatsQuery()

  const user: User = {
    email: loginDetails?.email,
    name: loginDetails?.name ?? undefined
  }

  const language = i18n.language
  const languagesRaw = i18n.options?.supportedLngs || [language]
  const languages = languagesRaw.filter((code) => code !== 'cimode')

  const navItems: NavItem[] = useMemo(() => {
    if (!pageAvailability || !plantCode) return []

    return pageAvailability
      .filter((page) => page.hasPermission)
      .map((page) => ({
        label: t(`navItems.${page.routeName}`),
        dataTestId: `nav_item_${page.routeName}`,
        url: getUrl(page.path, {plantCode}),
        isDisabled: !page.isAvailable,
        showBadge:
          page.routeName === RouteName.OPTIMIZER &&
          optimizedSchedulesStats &&
          optimizedSchedulesStats.notAccepted > 0,
        id: `nav_item_${page.routeName}`
      }))
  }, [pageAvailability, plantCode, optimizedSchedulesStats, t])

  const handleNavigate = useCallback(
    (nextPage: string) => {
      const currentPageKey = resolvePageKey(pathName)
      const nextPageKey = resolvePageKey(nextPage)

      trackPlannerEvent({
        name: 'userMovesPage',
        fromPage: currentPageKey && t(`navItems.${currentPageKey}`, {lng: 'en-US'}),
        toPage: nextPageKey && t(`navItems.${nextPageKey}`, {lng: 'en-US'})
      })
      navigate(nextPage)
    },
    [pathName, t, trackPlannerEvent, navigate]
  )

  const {themeMode} = useThemeSettings()

  if (!loginDetails || !permissions || !plantCode) return null

  return (
    <CommonHeader
      appName="Planner"
      plant={{plantId: plantCode, plantName: getPlantName(upmPlants, plantCode)}}
      upmPlants={upmPlants}
      guidedTours={guidedTours}
      env={process.env.REACT_APP_STAGE as Env}
      permissions={permissions}
      navItems={navItems}
      navigate={handleNavigate}
      locationPath={pathName}
      supportedLanguages={languages}
      themeMode={themeMode}
      user={user}
      onLogout={() => void logout()}
      onNavItemHover={(url) => {
        const matchedRouteName = Object.entries(mainRoutes).find(([, path]) => {
          const match = matchPath(path, url)
          return Boolean(match)
        })?.[0] as keyof MainRoutes | undefined

        if (matchedRouteName) {
          void subPageDynamicImportMap[matchedRouteName]()
        }
      }}
    />
  )
}
